/**
 * 📌 Script: DonationPickupContracts.js
 * 📂 Caminho: src/services/DonationPickupContracts.js
 * 📝 Descrição: Contratos puros de Abholspenden usados pelo frontend.
 *
 * 🔄 Relação com o Projeto:
 *  - Define status, regiões B1-B8 e localidades atendidas.
 *  - Será consumido pela página pública /spenden e cards internos.
 *
 * 📥 Entradas (Parâmetros):
 *  - `status` (String) - Status técnico a validar.
 *  - `routeCode` (String) - Código operacional B1-B8.
 *  - `postalCode` (String) - CEP da localidade atendida.
 *  - `city` (String) - Nome da localidade atendida.
 *
 * 📤 Saídas:
 *  - Constantes congeladas e helpers puros sem acesso a Firebase.
 *
 * 🧩 Exports:
 *  - `DONATION_PICKUP_PROTOCOL_STATUSES` - Status de protocolo.
 *  - `DONATION_PICKUP_ITEM_REVIEW_STATUSES` - Status de triagem.
 *  - `DONATION_PICKUP_ITEM_PICKUP_STATUSES` - Status de coleta.
 *  - `DONATION_PICKUP_ROUTE_CODES` - Códigos B1-B8.
 *  - `DONATION_PICKUP_ROUTE_REGIONS` - Localidades por região.
 *  - `DONATION_PICKUP_SUPPORTED_LOCATIONS` - CEPs/localidades.
 *  - `isDonationPickupProtocolStatus()` - Valida status de protocolo.
 *  - `isDonationPickupRouteCode()` - Valida código operacional.
 *  - `findDonationPickupLocation()` - Resolve CEP/localidade.
 *
 * 🔗 Dependências:
 *  - **Utilitários internos:** nenhum.
 *  - **Componentes Vue:** futuros cards e SpendenFormularView.vue.
 *  - **Backend (Cloud Functions/API):** nenhum nesta etapa.
 *  - **Bibliotecas externas (NPM):** nenhuma.
 */

export const DONATION_PICKUP_PROTOCOL_STATUSES = Object.freeze({
  PENDING_TRIAGE: "pending_triage",
  ACCEPTED_PENDING_SCHEDULE: "accepted_pending_schedule",
  REJECTED_FINISHED: "rejected_finished",
  SCHEDULED: "scheduled",
  PICKUP_COMPLETED_FINISHED: "pickup_completed_finished",
  PICKUP_NOT_COMPLETED_RESCHEDULE_PENDING:
    "pickup_not_completed_reschedule_pending",
  CANCELLED_FINISHED: "cancelled_finished",
  SENSITIVE_DATA_DELETED: "sensitive_data_deleted",
});

export const DONATION_PICKUP_ITEM_REVIEW_STATUSES = Object.freeze({
  PENDING_REVIEW: "pending_review",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
});

export const DONATION_PICKUP_ITEM_PICKUP_STATUSES = Object.freeze({
  PENDING_PICKUP: "pending_pickup",
  COLLECTED: "collected",
  DISCARDED_AT_PICKUP: "discarded_at_pickup",
  NOT_COLLECTED_NEW_PROTOCOL_REQUIRED: "not_collected_new_protocol_required",
});

export const DONATION_PICKUP_ROUTE_CODES = Object.freeze([
  "B1",
  "B2",
  "B3",
  "B4",
  "B5",
  "B6",
  "B7",
  "B8",
  "MR",
  "MRE",
]);

export const DONATION_PICKUP_PUBLIC_ROUTE_CODES = DONATION_PICKUP_ROUTE_CODES;

export const DONATION_PICKUP_INTERNAL_ROUTE_CODES = DONATION_PICKUP_ROUTE_CODES;

export const DONATION_PICKUP_ROUTE_REGIONS = Object.freeze({
  B1: Object.freeze([
    "Angelburg",
    "Biedenkopf",
    "Breidenbach",
    "Dautphetal",
    "Steffenberg",
  ]),
  B2: Object.freeze(["Bad Endbach", "Gladenbach", "Lohra"]),
  B3: Object.freeze(["Fronhausen", "Weimar"]),
  B4: Object.freeze(["Amöneburg", "Ebsdorfergrund"]),
  B5: Object.freeze(["Neustadt", "Stadtallendorf"]),
  B6: Object.freeze(["Cölbe", "Kirchhain"]),
  B7: Object.freeze(["Rauschenberg", "Wohratal"]),
  B8: Object.freeze(["Lahntal", "Münchhausen", "Wetter"]),
  MR: Object.freeze(["Marburg"]),
  MRE: Object.freeze(["Marburg MRE-Tour"]),
});

export const DONATION_PICKUP_SUPPORTED_LOCATIONS = Object.freeze([
  Object.freeze({ routeCode: "B1", postalCode: "35719", city: "Angelburg" }),
  Object.freeze({ routeCode: "B1", postalCode: "35216", city: "Biedenkopf" }),
  Object.freeze({ routeCode: "B1", postalCode: "35236", city: "Breidenbach" }),
  Object.freeze({ routeCode: "B1", postalCode: "35232", city: "Dautphetal" }),
  Object.freeze({ routeCode: "B1", postalCode: "35239", city: "Steffenberg" }),
  Object.freeze({ routeCode: "B2", postalCode: "35080", city: "Bad Endbach" }),
  Object.freeze({ routeCode: "B2", postalCode: "35075", city: "Gladenbach" }),
  Object.freeze({ routeCode: "B2", postalCode: "35102", city: "Lohra" }),
  Object.freeze({ routeCode: "B3", postalCode: "35112", city: "Fronhausen" }),
  Object.freeze({ routeCode: "B3", postalCode: "35096", city: "Weimar" }),
  Object.freeze({ routeCode: "B4", postalCode: "35287", city: "Amöneburg" }),
  Object.freeze({
    routeCode: "B4",
    postalCode: "35085",
    city: "Ebsdorfergrund",
  }),
  Object.freeze({ routeCode: "B5", postalCode: "35279", city: "Neustadt" }),
  Object.freeze({
    routeCode: "B5",
    postalCode: "35260",
    city: "Stadtallendorf",
  }),
  Object.freeze({ routeCode: "B6", postalCode: "35091", city: "Cölbe" }),
  Object.freeze({ routeCode: "B6", postalCode: "35274", city: "Kirchhain" }),
  Object.freeze({ routeCode: "B7", postalCode: "35282", city: "Rauschenberg" }),
  Object.freeze({ routeCode: "B7", postalCode: "35288", city: "Wohratal" }),
  Object.freeze({ routeCode: "B8", postalCode: "35094", city: "Lahntal" }),
  Object.freeze({
    routeCode: "B8",
    postalCode: "35117",
    city: "Münchhausen",
  }),
  Object.freeze({ routeCode: "B8", postalCode: "35083", city: "Wetter" }),
  Object.freeze({ routeCode: "MR", postalCode: "35037", city: "Marburg" }),
]);

export const DONATION_PICKUP_PUBLIC_SUPPORTED_LOCATIONS =
  DONATION_PICKUP_SUPPORTED_LOCATIONS;

export const DONATION_PICKUP_INTERNAL_SUPPORTED_LOCATIONS =
  DONATION_PICKUP_SUPPORTED_LOCATIONS;

function normalizeContractText(value) {
  return String(value || "").trim();
}

export function isDonationPickupProtocolStatus(status) {
  return Object.values(DONATION_PICKUP_PROTOCOL_STATUSES).includes(
    normalizeContractText(status)
  );
}

export function isDonationPickupRouteCode(routeCode) {
  return DONATION_PICKUP_ROUTE_CODES.includes(normalizeContractText(routeCode));
}

export function isInternalDonationPickupRouteCode(routeCode) {
  return DONATION_PICKUP_INTERNAL_ROUTE_CODES.includes(
    normalizeContractText(routeCode)
  );
}

export function findDonationPickupLocation(postalCode, city) {
  const normalizedPostalCode = normalizeContractText(postalCode);
  const normalizedCity = normalizeContractText(city).toLowerCase();

  return (
    DONATION_PICKUP_SUPPORTED_LOCATIONS.find(
      (location) =>
        location.postalCode === normalizedPostalCode &&
        location.city.toLowerCase() === normalizedCity
    ) || null
  );
}

export function findInternalDonationPickupLocation(
  postalCode,
  city,
  routeCode = null
) {
  const normalizedPostalCode = normalizeContractText(postalCode);
  const normalizedCity = normalizeContractText(city).toLowerCase();
  const normalizedRouteCode = normalizeContractText(routeCode);

  return (
    DONATION_PICKUP_INTERNAL_SUPPORTED_LOCATIONS.find(
      (location) =>
        location.postalCode === normalizedPostalCode &&
        location.city.toLowerCase() === normalizedCity &&
        (!normalizedRouteCode || location.routeCode === normalizedRouteCode)
    ) || null
  );
}

export const DONATION_PICKUP_STATIC_CATEGORIES = Object.freeze([
  Object.freeze({ id: "bett_matratzen", name: "Bett/Matratzen" }),
  Object.freeze({ id: "buecher", name: "Bücher" }),
  Object.freeze({
    id: "computer_computerzubehoer",
    name: "Computer/Computerzubehör",
  }),
  Object.freeze({
    id: "dekoartikel_bilder_spiegel",
    name: "Dekoartikel/Bilder/Spiegel",
  }),
  Object.freeze({ id: "e_kleingeraete", name: "E-Kleingeräte" }),
  Object.freeze({ id: "fernseher_hifi_audio", name: "Fernseher/HiFi Audio" }),
  Object.freeze({ id: "gartenmoebel", name: "Gartenmöbel" }),
  Object.freeze({ id: "geschirrspueler", name: "Geschirrspüler" }),
  Object.freeze({ id: "haushaltswaren", name: "Haushaltswaren" }),
  Object.freeze({ id: "kuechengeraete", name: "Küchengeräte" }),
  Object.freeze({
    id: "kuehlschraenke_gefriergeraete",
    name: "Kühlschränke/Gefriergeräte",
  }),
  Object.freeze({ id: "lampen", name: "Lampen" }),
  Object.freeze({ id: "moebel_allgemein", name: "Möbel allgemein" }),
  Object.freeze({ id: "regal_wohnwand", name: "Regal/Wohnwand" }),
  Object.freeze({
    id: "schrank_kommode_vitrine",
    name: "Schrank/Kommode/Vitrine",
  }),
  Object.freeze({ id: "sofa_sessel", name: "Sofa/Sessel" }),
  Object.freeze({
    id: "spielwaren_kinderartikel",
    name: "Spielwaren/Kinderartikel",
  }),
  Object.freeze({ id: "staubsauger", name: "Staubsauger" }),
  Object.freeze({ id: "teppich_textil", name: "Teppich/Textil" }),
  Object.freeze({ id: "tisch_stuhl", name: "Tisch/Stuhl" }),
  Object.freeze({
    id: "waschmaschine_trockner",
    name: "Waschmaschine/Trockner",
  }),
  Object.freeze({ id: "sonstiges", name: "Sonstiges" }),
]);
